import * as Sentry from '@sentry/browser'
import * as Integrations from '@sentry/integrations'

import ImplicitAuthProvider from '@revas/kadra-library/src/core/auth/ImplicitAuthProvider'
import AutosignTokenAuthProvider from '@revas/kadra-library/src/core/auth/AutosignTokenAuthProvider'
import SpaAuth0ImplicitAuthProvider from '@revas/kadra-library/src/core/auth/auth0/SpaAuth0ImplicitAuthProvider'
import { ulid } from 'ulid'

import { Client as RevasClient } from '@revas/ts-api'

import Vuex from 'vuex'
import { getModule } from 'vuex-module-decorators'

import AppStoreModule from '@revas/kadra-library/src/core/app/vue/vuex/AppStoreModule'

import AuthStoreModuleContainer from '@revas/kadra-library/src/core/auth/vue/vuex/AuthStoreModuleContainer'
import AuthStoreModule from '@revas/kadra-library/src/core/auth/vue/vuex/AuthStoreModule'

import CoreStoreModuleContainer from '@revas/kadra-library/src/revas/vue/vuex/CoreStoreModuleContainer'
import CoreStoreModule from '@revas/kadra-library/src/revas/vue/vuex/CoreStoreModule'

import OnboardingStoreModuleContainer from '@revas/kadra-library/src/revas/vue/vuex/OnboardingStoreModuleContainer'
import OnboardingStoreModule from '@revas/kadra-library/src/revas/vue/vuex/OnboardingStoreModule'

import DesktopsStoreModuleContainer from './store/DesktopsStoreModuleContainer'
import DesktopsStoreModule from './store/DesktopsStoreModule'

import LocaleProvider from '@revas/kadra-library/src/revas/traduko/vue/vue-i18n/LocaleProvider'

import OrganizationNavigator from '@revas/kadra-library/src/revas/vue/vue-router/OrganizationNavigator'
import VueRouter, { RouteConfig, RedirectOption, Route } from 'vue-router'

import axios from 'axios'
import { safeAxiosPost } from '../utils/safe-post'

import { worker } from '@/mocks/browser'
import '@/index.css'

import Vue from 'vue'
import App from './App.vue'

Vue.config.productionTip = false

interface DomainsConfiguration {
  testMode: boolean;
  baseUrl: string;
  sentryDsn: string;
  authSecret: string; // testing
  authDomain: string;
  authClientId: string;
  authAudience: string;
  i18nBaseUrl: string;
  baseApiUrl: string;
  isMockEnabled: boolean;
}

async function main () {
  const basePath = '/'
  const config: DomainsConfiguration = {
    testMode: process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test',
    baseUrl: `${window.location.origin}`,
    sentryDsn: process.env.VUE_APP_SENTRY_DSN || '',
    authSecret: process.env.VUE_APP_AUTH_SECRET || '',
    authDomain: process.env.VUE_APP_AUTH_DOMAIN || '',
    authClientId: process.env.VUE_APP_AUTH_CLIENT_ID || '',
    authAudience: process.env.VUE_APP_AUTH_AUDIENCE || '',
    i18nBaseUrl: process.env.VUE_APP_I18N_BASE_URL || 'https://os.revas.app/i18n',
    baseApiUrl: process.env.VUE_APP_REVAS_API_URL || '',
    isMockEnabled: process.env.VUE_APP_IS_MOCK_ENABLED === 'true'
  }

  // MONITORING SYSTEM
  if (!config.testMode && config.sentryDsn !== '') {
    Sentry.init({
      dsn: config.sentryDsn,
      integrations: [new Integrations.Vue({ Vue, attachProps: true })]
    })
  }

  // AUTHENTICATION
  let authProvider: ImplicitAuthProvider
  if (config.testMode && process.env.VUE_APP_TESTING_AUTH_SECRET !== undefined) {
    const uniqueId = process.env.VUE_APP_TESTING_AUTH_USER || ulid()
    authProvider = new AutosignTokenAuthProvider(process.env.VUE_APP_TESTING_AUTH_SECRET, uniqueId, `${uniqueId}@example.com`)
  } else {
    authProvider = new SpaAuth0ImplicitAuthProvider({
      domain: config.authDomain,
      // eslint-disable-next-line @typescript-eslint/camelcase
      client_id: config.authClientId,
      // eslint-disable-next-line @typescript-eslint/camelcase
      redirect_uri: config.baseUrl + basePath,
      audience: config.authAudience,
      scope: 'openid profile email',
      useRefreshTokens: true,
      cacheLocation: 'localstorage'
    })
  }

  if (!await authProvider.asyncIsAuthenticated()) {
    try {
      await authProvider.asyncHandle()
    } catch (e) {
      authProvider.redirect()
      // TODO add mountPanicApplication function
      // await mountPanicApplication(config, 'panic.redirect_to_authentication')
      return
    }
  }

  // // MOCK API
  if (config.testMode && config.isMockEnabled) {
    worker.start()
  }

  // REVAS CLIENT
  const revasClient = new RevasClient({
    apiUrl: config.baseApiUrl,
    testMode: config.testMode,
    accessToken: () => {
      return authProvider.asyncGetToken()
    }
  })

  // AXIOS CLIENT
  const axiosInstance = axios.create({
    baseURL: config.baseApiUrl
  })

  // INTERNATIONALIZATION
  const locale = new LocaleProvider(config.i18nBaseUrl, ['panic', 'error', 'shared', 'kadra', 'desktops', 'launchpad'])
  await locale.asyncLoadLanguage('it-IT')

  // VUEX STORE
  Vue.use(Vuex)
  const store = new Vuex.Store({
    modules: {
      app: AppStoreModule,
      onboarding: OnboardingStoreModule,
      auth: AuthStoreModule,
      core: CoreStoreModule,
      desktops: DesktopsStoreModule
    }
  })

  const appStoreModule = getModule(AppStoreModule, store)

  // If there are errors let the app store module inform the user.
  // The UI should show a correct experience even if the store is not initialized.
  const authStoreModuleContainer = new AuthStoreModuleContainer(authProvider)
  const authStoreModule = getModule(AuthStoreModule, store)
  authStoreModule.inject(authStoreModuleContainer)

  const coreStoreModuleContainer = new CoreStoreModuleContainer(appStoreModule, revasClient)
  const coreStoreModule = getModule(CoreStoreModule, store)
  coreStoreModule.inject(coreStoreModuleContainer)

  await coreStoreModule.asyncGetUserProfile()
  await coreStoreModule.asyncListUserOrganizations()

  revasClient.setGlobalOptions({
    organizationId: () => {
      if (coreStoreModule.activeUserOrganization === null) {
        return Promise.resolve('')
      } else {
        return Promise.resolve(coreStoreModule.activeUserOrganization.id)
      }
    }
  })

  axiosInstance.interceptors.request.use(async function (config) {
    // Do something before request is sent
    let organizationId = ''

    if (coreStoreModule.activeUserOrganization !== null) {
      organizationId = coreStoreModule.activeUserOrganization.id
    }
    config.data.organizationId = organizationId

    const token = await authProvider.asyncGetToken()
    config.headers.common.Authorization = `Bearer ${token}`
    config.headers.common['Revas-Organization-ID'] = organizationId
    return config
  }, function (error) {
    // Do something with request error
    return Promise.reject(error)
  })

  // SYSTEM INFO
  const [systemInfo, systemInfoErr] = await safeAxiosPost<any>(axiosInstance, '/chaos/chaos.SystemInfo/', {})

  // ONBOARDING
  const onboardingStoreModuleContainer = new OnboardingStoreModuleContainer(coreStoreModule, 'other')
  const onboardingStoreModule = getModule(OnboardingStoreModule, store)
  onboardingStoreModule.inject(onboardingStoreModuleContainer)

  const desktopsStoreModuleContainer = new DesktopsStoreModuleContainer(coreStoreModule, appStoreModule, revasClient, axiosInstance, locale.i18n)
  const desktopsStoreModule = getModule(DesktopsStoreModule, store)
  desktopsStoreModule.inject(desktopsStoreModuleContainer)

  // ROUTER
  Vue.use(VueRouter)
  const router = new VueRouter({
    base: basePath
  })

  const defaultViewName = 'desktops'
  const navigator = new OrganizationNavigator(router, coreStoreModule, defaultViewName, config.baseUrl)

  const redirectOption: RedirectOption = {
    name: defaultViewName,
    params: {
      organizationId: '-'
    }
  }

  const childrenRoutes: RouteConfig[] = [
    {
      name: defaultViewName,
      path: 'a',
      props: (route: Route) => ({
        coreStoreModule,
        appStoreModule,
        desktopsStoreModule,
        i18n: locale.i18n,
        router: navigator,
        routeName: route.name,
        organizationId: route.params.organizationId,
        baseUrl: config.baseUrl
      }),
      component: () => import('./views/VAppsCollection.vue')
    },
    {
      name: 'shop',
      path: 's',
      props: (route: Route) => ({
        coreStoreModule,
        appStoreModule,
        desktopsStoreModule,
        i18n: locale.i18n,
        router: navigator,
        routeName: route.name,
        domainId: route.params.domainId
      }),
      component: () => import('./views/VShopViewer.vue')
    }
  ]

  // Url remapping when query string

  function getUrl (path: string) {
    const href = window.location.href
    const hashPos = href.indexOf('#')
    let base = hashPos > -1 ? href.slice(0, hashPos) : href

    const searchPos = base.indexOf('?')
    const query = searchPos > -1 ? base.slice(searchPos) : ''
    base = query ? base.slice(0, searchPos) : base

    return `${base}#${path + query}`
  }

  if (location.search) {
    location.replace(getUrl(window.location.pathname))
  }

  // Build routes
  const routes: RouteConfig[] = []

  routes.push({
    path: '/o/:organizationId',
    props: (route: Route) => ({
      authStoreModule,
      coreStoreModule,
      appStoreModule,
      i18n: locale.i18n,
      navigator,
      organizationId: route.params.organizationId,
      baseUrl: config.baseUrl,
      systemInfo: systemInfo
    }),
    component: () => import('./layouts/OrganizationLayout.vue'),
    redirect: redirectOption,
    children: childrenRoutes
  })

  routes.push({
    path: '*',
    redirect: redirectOption
  })

  router.addRoutes(routes)

  new Vue({
    router,
    store,
    i18n: locale.i18n,
    render: h => h(App,
      {
        props: {
          appStoreModule,
          onboardingStoreModule,
          i18n: locale.i18n,
          systemInfo
        }
      })
  }).$mount('#app')
}

main()
